// @ts-ignore
/* eslint-disable */
import { request } from '@umijs/max';

const API_BASE_URL = '/api/v1';

// 款项方向类型
export type PaymentDirection = 'payment' | 'receipt';

// 关联合同信息（前置声明）
export interface ContractRelation {
  id: number;
  contract_id: number;
  related_contract_id: number;
  relation_type: ContractRelationType;
  remark?: string;
  created_by: number;
  created_at: string;
  related_contract_no: string;
  related_contract_name: string;
  related_contract_status: string;
}

// 关联类型
export type ContractRelationType = 'related' | 'supplement' | 'renewal' | 'parent' | 'child';

export interface Contract {
  id: number;
  contract_no: string;
  contract_name: string;
  contract_type: string;
  party_a: string;
  party_b: string;
  amount?: number;
  currency: string;
  start_date?: string;
  end_date?: string;
  status: string;
  description?: string;
  file_path?: string;
  payment_method?: string;
  payment_config?: any;
  payment_direction?: PaymentDirection;  // 款项方向：payment-付款合同，receipt-收款合同
  file_id?: string;
  file_name?: string;
  file_size?: number;
  department_id?: number;
  manager_user_id?: number;
  archived?: boolean;
  archive_document_id?: number;
  archive_retention_years?: number;
  archived_at?: string;
  archived_by?: number;
  created_by?: number;
  created_at: string;
  updated_at: string;
  // 归档权限相关
  can_archive?: boolean;
  selectable_storage_department_ids?: number[];
  // 关联合同
  related_contracts?: ContractRelation[];
}

export interface ContractListParams {
  page?: number;
  page_size?: number;
  status?: string;
  contract_type?: string;
  keyword?: string;
  payment_direction?: PaymentDirection;  // 按款项方向筛选
}

export interface ContractStatistics {
  total: number;
  effective: number;
  expired: number;
  expiring_soon: number;
  draft: number;
}

// 创建合同时的关联合同项
export interface RelatedContractInput {
  contract_id: number;
  relation_type?: ContractRelationType;
  remark?: string;
}

export interface ContractCreateParams {
  contract_name: string;
  contract_type: string;
  party_a: string;
  party_b: string;
  department_id: number;  // 必填，用于自动生成合同编号
  year?: string;  // 年份，用于生成合同编号
  amount?: number;
  currency?: string;
  start_date?: string;
  end_date?: string;
  status?: string;
  description?: string;
  file_path?: string;
  payment_method?: string;
  payment_config?: any;
  payment_direction?: PaymentDirection;  // 款项方向：payment-付款合同，receipt-收款合同
  file_id?: string;
  file_name?: string;
  file_size?: number;
  manager_user_id?: number;
  related_contracts?: RelatedContractInput[];  // 关联合同列表
}

export interface ContractUpdateParams {
  contract_name?: string;
  contract_type?: string;
  party_a?: string;
  party_b?: string;
  amount?: number;
  currency?: string;
  start_date?: string;
  end_date?: string;
  status?: string;
  description?: string;
  file_path?: string;
  payment_method?: string;
  payment_config?: any;
  payment_direction?: PaymentDirection;  // 款项方向：payment-付款合同，receipt-收款合同
  file_id?: string;
  file_name?: string;
  file_size?: number;
  department_id?: number;
  manager_user_id?: number;
}

// 记录类型
export type RecordType = 'payment' | 'receipt';

// 收款类型：rent-租金，property_fee-物业费，deposit-押金，null-未设置
export type ReceiptType = 'rent' | 'property_fee' | 'deposit' | null;

// 收款类型选项
export const RECEIPT_TYPE_OPTIONS = [
  { value: 'rent', label: '租金' },
  { value: 'property_fee', label: '物业费' },
  { value: 'deposit', label: '押金' },
];

export interface ContractPayment {
  id: number;
  contract_id: number;
  record_type?: RecordType;  // 记录类型：payment-付款记录，receipt-收款记录
  payment_phase?: string;
  payment_amount: number;
  payment_ratio?: number;
  due_date?: string;
  actual_payment_date?: string;
  payment_status: string;
  reminder_sent: boolean;
  reminder_sent_at?: string;
  remark?: string;
  receipt_type?: ReceiptType;  // 收款类型：rent-租金，property_fee-物业费，deposit-押金
  is_refunded?: boolean;  // 是否已退款
  refunded_at?: string;  // 退款时间
  created_at: string;
  updated_at: string;
  // 关联合同信息（用于即将到期付款列表）
  contract_no?: string;
  contract_name?: string;
  party_a?: string;
  party_b?: string;
}

export interface ContractPaymentCreateParams {
  contract_id: number;
  record_type?: RecordType;  // 记录类型：payment-付款记录，receipt-收款记录
  payment_phase?: string;
  payment_amount: number;
  payment_ratio?: number;
  due_date?: string;
  actual_payment_date?: string;
  payment_status?: string;
  remark?: string;
  receipt_type?: ReceiptType;  // 收款类型：rent-租金，property_fee-物业费，deposit-押金
}

export interface ArchiveContractParams {
  retention_years: number;
  storage_department_id?: number;
  remark?: string;
}

export interface ContractAdmin {
  id: number;
  user_id: number;
  user_name?: string;
  department_id?: number;
  department_name?: string;
  admin_type: string;
  created_by: number;
  created_at: string;
  updated_at: string;
}

export interface ContractAdminCreate {
  user_id: number;
  department_id?: number;
  admin_type: string;
}

export interface ContractAdminListParams {
  department_id?: number;
  admin_type?: string;
}

// ========== 合同关联相关类型 ==========

// 关联类型选项
export const RELATION_TYPE_OPTIONS = [
  { value: 'related', label: '相关合同' },
  { value: 'supplement', label: '补充合同' },
  { value: 'renewal', label: '续签合同' },
  { value: 'parent', label: '主合同' },
  { value: 'child', label: '子合同' },
];

// 添加关联合同请求参数
export interface AddContractRelationParams {
  related_contract_id: number;
  relation_type?: ContractRelationType;
  remark?: string;
}

/** 获取合同列表 */
export async function getContracts(params: ContractListParams) {
  return request<OA.ApiResponse<{ items: Contract[]; total: number; page: number; page_size: number }>>(`${API_BASE_URL}/contracts/`, {
    method: 'GET',
    params,
  });
}

/** 获取合同详情 */
export async function getContract(id: number) {
  return request<OA.ApiResponse<Contract>>(`${API_BASE_URL}/contracts/${id}`, {
    method: 'GET',
  });
}

/** 创建合同 */
export async function createContract(data: ContractCreateParams) {
  return request<OA.ApiResponse<Contract>>(`${API_BASE_URL}/contracts/`, {
    method: 'POST',
    data,
  });
}

/** 更新合同 */
export async function updateContract(id: number, data: ContractUpdateParams) {
  return request<OA.ApiResponse<Contract>>(`${API_BASE_URL}/contracts/${id}`, {
    method: 'PUT',
    data,
  });
}

/** 删除合同 */
export async function deleteContract(id: number) {
  return request<OA.ApiResponse<any>>(`${API_BASE_URL}/contracts/${id}`, {
    method: 'DELETE',
  });
}

/** 获取合同统计信息 */
export async function getContractStatistics() {
  return request<OA.ApiResponse<ContractStatistics>>(`${API_BASE_URL}/contracts/statistics`, {
    method: 'GET',
  });
}

/** 获取即将到期的合同 */
export async function getExpiringContracts(days: number = 7) {
  return request<OA.ApiResponse<Contract[]>>(`${API_BASE_URL}/contracts/expiring`, {
    method: 'GET',
    params: { days },
  });
}

/** 上传合同文件 */
export async function uploadContractFile(base64_data: string, filename: string) {
  return request<OA.ApiResponse<any>>(`${API_BASE_URL}/contracts/upload-file`, {
    method: 'POST',
    data: { base64_data, filename },
  });
}

/** 获取合同付款记录 */
export async function getContractPayments(contractId: number) {
  return request<OA.ApiResponse<ContractPayment[]>>(`${API_BASE_URL}/contracts/${contractId}/payments`, {
    method: 'GET',
  });
}

/** 创建付款记录 */
export async function createContractPayment(contractId: number, data: ContractPaymentCreateParams) {
  return request<OA.ApiResponse<any>>(`${API_BASE_URL}/contracts/${contractId}/payments`, {
    method: 'POST',
    data,
  });
}

/** 更新付款记录 */
export async function updateContractPayment(paymentId: number, data: Partial<ContractPaymentCreateParams>) {
  return request<OA.ApiResponse<any>>(`${API_BASE_URL}/contracts/payments/${paymentId}`, {
    method: 'PUT',
    data,
  });
}

/** 删除付款记录 */
export async function deleteContractPayment(paymentId: number) {
  return request<OA.ApiResponse<any>>(`${API_BASE_URL}/contracts/payments/${paymentId}`, {
    method: 'DELETE',
  });
}

/** 押金退款 */
export async function refundContractPayment(paymentId: number) {
  return request<OA.ApiResponse<ContractPayment>>(`${API_BASE_URL}/contracts/payments/${paymentId}/refund`, {
    method: 'POST',
  });
}

/** 归档合同到文书档案 */
export async function archiveContract(contractId: number, data: ArchiveContractParams) {
  return request<OA.ApiResponse<any>>(`${API_BASE_URL}/contracts/${contractId}/archive`, {
    method: 'POST',
    data,
  });
}

/** 获取合同管理员列表 */
export async function getContractAdmins(params?: ContractAdminListParams) {
  return request<OA.ApiResponse<ContractAdmin[]>>(`${API_BASE_URL}/contracts/admins/list`, {
    method: 'GET',
    params,
  });
}

/** 添加合同管理员 */
export async function addContractAdmin(data: ContractAdminCreate) {
  return request<OA.ApiResponse<ContractAdmin>>(`${API_BASE_URL}/contracts/admins/add`, {
    method: 'POST',
    data,
  });
}

/** 移除合同管理员 */
export async function removeContractAdmin(adminId: number) {
  return request<OA.ApiResponse<any>>(`${API_BASE_URL}/contracts/admins/remove/${adminId}`, {
    method: 'DELETE',
  });
}

/** 获取即将到期的付款 */
export async function getUpcomingPayments(days: number = 7) {
  return request<OA.ApiResponse<ContractPayment[]>>(`${API_BASE_URL}/contracts/payments/upcoming`, {
    method: 'GET',
    params: { days },
  });
}

/** 获取已逾期的付款 */
export async function getOverduePayments() {
  return request<OA.ApiResponse<ContractPayment[]>>(`${API_BASE_URL}/contracts/payments/overdue`, {
    method: 'GET',
  });
}

/** 批量创建付款记录 */
export interface BatchPaymentCreateParams {
  contract_id: number;
  payments: ContractPaymentCreateParams[];
}

export async function batchCreateContractPayments(data: BatchPaymentCreateParams) {
  return request<OA.ApiResponse<ContractPayment[]>>(`${API_BASE_URL}/contracts/payments/batch`, {
    method: 'POST',
    data,
  });
}

// ========== 部门前缀配置相关接口 ==========

export interface ContractDepartmentPrefix {
  id: number;
  department_id: number;
  prefix: string;
  department_name?: string;
  created_by: number;
  created_at: string;
  updated_at: string;
}

export interface ContractDepartmentPrefixCreate {
  department_id: number;
  prefix: string;
}

export interface ContractDepartmentPrefixUpdate {
  prefix: string;
}

/** 获取所有部门前缀配置 */
export async function getContractPrefixList() {
  return request<OA.ApiResponse<ContractDepartmentPrefix[]>>(`${API_BASE_URL}/contracts/prefix/list`, {
    method: 'GET',
  });
}

/** 创建部门前缀配置 */
export async function createContractPrefix(data: ContractDepartmentPrefixCreate) {
  return request<OA.ApiResponse<ContractDepartmentPrefix>>(`${API_BASE_URL}/contracts/prefix`, {
    method: 'POST',
    data,
  });
}

/** 更新部门前缀配置 */
export async function updateContractPrefix(configId: number, data: ContractDepartmentPrefixUpdate) {
  return request<OA.ApiResponse<ContractDepartmentPrefix>>(`${API_BASE_URL}/contracts/prefix/${configId}`, {
    method: 'PUT',
    data,
  });
}

/** 删除部门前缀配置 */
export async function deleteContractPrefix(configId: number) {
  return request<OA.ApiResponse<any>>(`${API_BASE_URL}/contracts/prefix/${configId}`, {
    method: 'DELETE',
  });
}

// ========== 合同编号序列配置相关接口 ==========

export interface ContractNumberConfig {
  id: number;
  department_id: number;
  department_prefix: string;
  year_key: string;
  current_sequence: number;
  department_name: string;
  created_at: string;
  updated_at: string;
}

export interface ContractNumberConfigListParams {
  department_id?: number;
  year_key?: string;
}

export interface ContractNumberConfigUpdateParams {
  current_sequence: number;
}

/** 获取合同编号配置列表 */
export async function getContractNumberConfigs(params?: ContractNumberConfigListParams) {
  return request<OA.ApiResponse<ContractNumberConfig[]>>(`${API_BASE_URL}/contracts/number-config`, {
    method: 'GET',
    params,
  });
}

/** 更新合同编号序列值 */
export async function updateContractNumberConfig(configId: number, data: ContractNumberConfigUpdateParams) {
  return request<OA.ApiResponse<ContractNumberConfig>>(`${API_BASE_URL}/contracts/number-config/${configId}`, {
    method: 'PUT',
    data,
  });
}

// ========== 合同关联相关接口 ==========

/** 获取合同关联列表 */
export async function getContractRelations(contractId: number) {
  return request<OA.ApiResponse<ContractRelation[]>>(`${API_BASE_URL}/contracts/${contractId}/relations`, {
    method: 'GET',
  });
}

/** 添加合同关联 */
export async function addContractRelation(contractId: number, data: AddContractRelationParams) {
  return request<OA.ApiResponse<ContractRelation>>(`${API_BASE_URL}/contracts/${contractId}/relations`, {
    method: 'POST',
    data,
  });
}

/** 移除合同关联 */
export async function removeContractRelation(contractId: number, relatedContractId: number) {
  return request<OA.ApiResponse<any>>(`${API_BASE_URL}/contracts/${contractId}/relations/${relatedContractId}`, {
    method: 'DELETE',
  });
}

/** 按月导出合同 */
export async function exportContracts(
  params: { year: number; month: number },
  options?: { [key: string]: any },
) {
  return request<Blob>(
    `${API_BASE_URL}/contracts/export`,
    {
      method: 'GET',
      params,
      responseType: 'blob',
      ...(options || {}),
    },
  );
}
